<script setup lang="ts">
  import {
    type SuperUserOrganisation as Organisation,
    type PatchedSuperUserOrganisationRequest as PatchedOrganisationRequest
  } from '@/types/generated/types.gen'
  import { OrganisationsService } from '@/types/generated/services.gen'
  import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import ColorPickerField from '@/components/admin/form/ColorPickerField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import router from '@/router'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import * as yup from 'yup'
  import { useTranslation } from '@/composables/useTranslation'
  import FormGrid from '@/components/admin/form/FormGrid.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { toast } from '@/components/ui/toast'
  import { Copy, Trash2 } from 'lucide-vue-next'

  const { t } = useTranslation({
    en: {
      botSettings: 'Bot Settings',
      embedScript: 'Embed Script',
      copyScript: 'Copy Script',
      scriptCopied: 'Script copied to clipboard!',
      chatSettings: 'Chat Settings',
      autoOpenChat: 'Auto-open Chat',
      autoOpenDelay: 'Auto-open Delay (ms)',
      initialMessages: 'Initial Messages',
      initialButtons: 'Initial Action Buttons',
      addMessage: 'Add Message',
      addButton: 'Add Button',
      messageText: 'Message Text',
      buttonText: 'Button Text',
      buttonLink: 'Button Link (URL)',
      design: 'Design & Colors',
      headerBackgroundColor: 'Header Background Color',
      headerTextColor: 'Header Text Color',
      botBubbleColor: 'Bot Bubble Color',
      botBubbleTextColor: 'Bot Bubble Text Color',
      userBubbleColor: 'User Bubble Color',
      userBubbleTextColor: 'User Bubble Text Color',
      chatBackgroundColor: 'Chat Background Color',
      inputBackgroundColor: 'Input Background Color',
      inputTextColor: 'Input Text Color',
      customFont: 'Custom Font Family',
      footerContent: 'Footer Content (HTML)',
      websiteButton: 'Website Button',
      buttonPosition: 'Button Position',
      buttonBackgroundColor: 'Button Background Color',
      buttonTextColor: 'Button Text Color',
      customButton: 'Custom Button',
      customButtonText: 'Custom Button Text',
      customButtonWidth: 'Button Width (px)',
      customButtonHeight: 'Button Height (px)',
      customButtonBgColor: 'Custom Button Background',
      customButtonTextColor: 'Custom Button Text Color',
      customButtonCSS: 'Custom Button CSS',
      preview: 'Preview',
      save: 'Save',
      bottomRight: 'Bottom Right',
      bottomLeft: 'Bottom Left',
      error: 'Error',
      loadError: 'Failed to load bot settings. Please try again.',
      saveError: 'Failed to save bot settings. Please try again.',
      chatFunctions: 'Chat Functions',
      chatFunctionsDesc: 'JavaScript functions to control the chat window:',
      openChat: 'Open',
      closeChat: 'Close',
      toggleChat: 'Toggle',
      buttonFunctions: 'Button Functions',
      buttonFunctionsDesc: 'JavaScript functions to control the chat button:',
      showButton: 'Show',
      hideButton: 'Hide',
      toggleButton: 'Toggle',
      saving: 'Saving...',
      pleaseWait: 'Please wait',
      saved: 'Saved!'
    },
    de: {
      botSettings: 'Bot Einstellungen',
      embedScript: 'Einbindungs-Script',
      copyScript: 'Script kopieren',
      scriptCopied: 'Script in Zwischenablage kopiert!',
      chatSettings: 'Chat Einstellungen',
      autoOpenChat: 'Chat automatisch öffnen',
      autoOpenDelay: 'Automatische Öffnungsverzögerung (ms)',
      initialMessages: 'Initiale Nachrichten',
      initialButtons: 'Initiale Aktionsbuttons',
      addMessage: 'Nachricht hinzufügen',
      addButton: 'Button hinzufügen',
      messageText: 'Nachrichtentext',
      buttonText: 'Button Text',
      buttonLink: 'Button Link (URL)',
      design: 'Design & Farben',
      headerBackgroundColor: 'Header Hintergrundfarbe',
      headerTextColor: 'Header Textfarbe',
      botBubbleColor: 'Bot Sprechblase Farbe',
      botBubbleTextColor: 'Bot Sprechblase Textfarbe',
      userBubbleColor: 'Benutzer Sprechblase Farbe',
      userBubbleTextColor: 'Benutzer Sprechblase Textfarbe',
      chatBackgroundColor: 'Chat Hintergrundfarbe',
      inputBackgroundColor: 'Eingabe Hintergrundfarbe',
      inputTextColor: 'Eingabe Textfarbe',
      customFont: 'Benutzerdefinierte Schriftart',
      footerContent: 'Footer Inhalt (HTML)',
      websiteButton: 'Website Button',
      buttonPosition: 'Button Position',
      buttonBackgroundColor: 'Button Hintergrundfarbe',
      buttonTextColor: 'Button Textfarbe',
      customButton: 'Benutzerdefinierter Button',
      customButtonText: 'Benutzerdefinierter Button Text',
      customButtonWidth: 'Button Breite (px)',
      customButtonHeight: 'Button Höhe (px)',
      customButtonBgColor: 'Benutzerdefinierter Button Hintergrund',
      customButtonTextColor: 'Benutzerdefinierter Button Textfarbe',
      customButtonCSS: 'Benutzerdefiniertes Button CSS',
      preview: 'Vorschau',
      save: 'Speichern',
      bottomRight: 'Unten Rechts',
      bottomLeft: 'Unten Links',
      error: 'Fehler',
      loadError: 'Fehler beim Laden der Bot-Einstellungen. Bitte versuchen Sie es erneut.',
      saveError: 'Fehler beim Speichern der Bot-Einstellungen. Bitte versuchen Sie es erneut.',
      chatFunctions: 'Chat Funktionen',
      chatFunctionsDesc: 'JavaScript-Funktionen zur Steuerung des Chat-Fensters:',
      openChat: 'Öffnen',
      closeChat: 'Schließen',
      toggleChat: 'Umschalten',
      buttonFunctions: 'Button Funktionen',
      buttonFunctionsDesc: 'JavaScript-Funktionen zur Steuerung des Chat-Buttons:',
      showButton: 'Anzeigen',
      hideButton: 'Ausblenden',
      toggleButton: 'Umschalten',
      saving: 'Wird gespeichert...',
      pleaseWait: 'Bitte warten',
      saved: 'Gespeichert!'
    }
  })

  const org = ref<Organisation | undefined>(undefined)
  const isMounted = ref(false)
  const currentBotScript = ref<HTMLScriptElement | null>(null)
  const initialMessages = ref<Array<{ text: string }>>([])
  const initialButtons = ref<Array<{ title: string; payload: string }>>([])

  const BOT_ELEMENT_IDS = [
    'botario-chat-floating-button',
    'botario-chat-window',
    'botario-chat-widget',
    'botario',
    'botario-iframe',
    'botario-container'
  ]

  const hideBotUI = () => {
    if (currentBotScript.value) {
      currentBotScript.value.remove()
      currentBotScript.value = null
    }

    BOT_ELEMENT_IDS.forEach((id) => {
      const el = document.getElementById(id)
      if (el) {
        ;(el as HTMLElement).style.display = 'none'
      }
    })
  }

  const removeBotCompletely = () => {
    if (currentBotScript.value) {
      currentBotScript.value.remove()
      currentBotScript.value = null
    }

    document.querySelectorAll('script[src*="chatbot/embed"]').forEach((el) => el.remove())
    document.getElementById('jupus-chatbot-embed')?.remove()

    BOT_ELEMENT_IDS.forEach((id) => {
      document.getElementById(id)?.remove()
    })

    const keysToRemove: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && (key.startsWith('btr') || key.startsWith('jp_'))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key))
  }

  const embedScript = computed(() => {
    if (!org.value) return ''
    const uuid = org.value.bot_embed_uuid
    return (
      `<script>
  (function(w,d,j){
    w[j]=w[j]||function(){(w[j].q=w[j].q||[]).push(arguments)};
    var s=d.getElementsByTagName('script')[0],U=d.createElement('script');
    U.async=1;
    U.src='${window.location.protocol}//${window.location.host}/channels/ingress/chatbot/embed/${uuid}.js';
    s.parentNode.insertBefore(U,s)}(window,document,'jp'));
<` + `/script>`
    )
  })

  const loadBotScript = () => {
    if (!org.value || !isMounted.value) return

    const script = document.createElement('script')
    const timestamp = Date.now()
    script.innerHTML = `
    (function(w,d,j){
      w[j]=w[j]||function(){(w[j].q=w[j].q||[]).push(arguments)};
      var s=d.getElementsByTagName('script')[0],U=d.createElement('script');
      U.async=1;
      U.src='${window.location.protocol}//${window.location.host}/channels/ingress/chatbot/embed/${org.value.bot_embed_uuid}.js?t=${timestamp}';
      s.parentNode.insertBefore(U,s)}(window,document,'jp'));
  `
    document.body.appendChild(script)
    currentBotScript.value = script
  }

  const buttonPositionOptions = [
    { value: 'bottom-right', title: 'Bottom Right' },
    { value: 'bottom-left', title: 'Bottom Left' }
  ]

  onMounted(async () => {
    isMounted.value = true

    try {
      org.value = (await OrganisationsService.organisationsRetrieve({
        uuid: router.currentRoute.value.params.uuid.toString()
      })) as Organisation

      if (org.value.bot_start_without_bot && Array.isArray(org.value.bot_start_without_bot)) {
        initialMessages.value = org.value.bot_start_without_bot
          .map((msg: any) => ({ text: msg.text || msg }))
          .filter((msg) => msg.text && msg.text.trim())
      }
      if (
        org.value.bot_start_without_bot_buttons &&
        Array.isArray(org.value.bot_start_without_bot_buttons)
      ) {
        initialButtons.value = org.value.bot_start_without_bot_buttons
          .map((btn: any) => ({ title: btn.title || '', payload: btn.payload || '' }))
          .filter((btn) => (btn.title && btn.title.trim()) || (btn.payload && btn.payload.trim()))
      }

      if (isMounted.value) {
        loadBotScript()
      }
    } catch (error) {
      if (isMounted.value) {
        toast({ title: t('error'), description: t('loadError'), variant: 'destructive' })
      }
    }
  })

  onBeforeUnmount(() => {
    isMounted.value = false
    removeBotCompletely()
  })

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(embedScript.value)
      toast({
        title: t('scriptCopied'),
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: t('error'),
        variant: 'destructive'
      })
    }
  }

  const addMessage = () => {
    initialMessages.value.push({ text: '' })
  }

  const removeMessage = (index: number) => {
    initialMessages.value.splice(index, 1)
  }

  const addButton = () => {
    initialButtons.value.push({ title: '', payload: '' })
  }

  const removeButton = (index: number) => {
    initialButtons.value.splice(index, 1)
  }

  const saveAndRefresh = async () => {
    if (!isMounted.value || !org.value) return

    toast({ title: t('saving'), description: t('pleaseWait'), variant: 'default' })

    try {
      hideBotUI()

      const filteredMessages = initialMessages.value.filter((msg) => msg.text && msg.text.trim())
      const filteredButtons = initialButtons.value.filter(
        (btn) => (btn.title && btn.title.trim()) || (btn.payload && btn.payload.trim())
      )

      await OrganisationsService.organisationsPartialUpdate({
        uuid: org.value.uuid,
        requestBody: {
          bot_chat_auto_open: org.value.bot_chat_auto_open,
          bot_chat_auto_open_delay: org.value.bot_chat_auto_open_delay,
          bot_title_background_color: org.value.bot_title_background_color,
          bot_title_text_color: org.value.bot_title_text_color,
          bot_chat_bubble_operator_color: org.value.bot_chat_bubble_operator_color,
          bot_chat_bubble_operator_text_color: org.value.bot_chat_bubble_operator_text_color,
          bot_chat_bubble_contact_color: org.value.bot_chat_bubble_contact_color,
          bot_chat_bubble_contact_text_color: org.value.bot_chat_bubble_contact_text_color,
          bot_background_color: org.value.bot_background_color,
          bot_message_input_background_color: org.value.bot_message_input_background_color,
          bot_message_input_text_color: org.value.bot_message_input_text_color,
          bot_custom_font_family: org.value.bot_custom_font_family,
          bot_footer_content: org.value.bot_footer_content,
          bot_button_position: org.value.bot_button_position,
          bot_button_background_color: org.value.bot_button_background_color,
          bot_button_text_color: org.value.bot_button_text_color,
          bot_floating_custom_button_text: org.value.bot_floating_custom_button_text,
          bot_floating_custom_button_width: org.value.bot_floating_custom_button_width,
          bot_floating_custom_button_height: org.value.bot_floating_custom_button_height,
          bot_floating_custom_button_background_color:
            org.value.bot_floating_custom_button_background_color,
          bot_floating_custom_button_text_color: org.value.bot_floating_custom_button_text_color,
          bot_floating_custom: org.value.bot_floating_custom,
          bot_start_without_bot: filteredMessages,
          bot_start_without_bot_buttons: filteredButtons
        } as PatchedOrganisationRequest
      })

      toast({ title: t('saved'), variant: 'default' })

      await new Promise((resolve) => setTimeout(resolve, 3000))

      if (!isMounted.value) return

      removeBotCompletely()

      org.value = (await OrganisationsService.organisationsRetrieve({
        uuid: org.value.uuid
      })) as Organisation

      if (org.value.bot_start_without_bot && Array.isArray(org.value.bot_start_without_bot)) {
        initialMessages.value = org.value.bot_start_without_bot
          .map((msg: any) => ({ text: msg.text || msg }))
          .filter((msg) => msg.text && msg.text.trim())
      }
      if (
        org.value.bot_start_without_bot_buttons &&
        Array.isArray(org.value.bot_start_without_bot_buttons)
      ) {
        initialButtons.value = org.value.bot_start_without_bot_buttons
          .map((btn: any) => ({ title: btn.title || '', payload: btn.payload || '' }))
          .filter((btn) => (btn.title && btn.title.trim()) || (btn.payload && btn.payload.trim()))
      }

      if (isMounted.value) {
        loadBotScript()
      }
    } catch (error) {
      toast({ title: t('error'), description: t('saveError'), variant: 'destructive' })
    }
  }
</script>

<template>
  <div
    v-if="org"
    class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5"
  >
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Customers', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST },
          {
            title: org.name || org.uuid,
            to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_DETAIL.replace(':uuid', org.uuid)
          },
          { title: t('botSettings'), to: '#' }
        ]"
      />
      <button
        type="button"
        class="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white
          transition-colors hover:bg-purple-700"
        @click="saveAndRefresh"
      >
        {{ t('save') }}
      </button>
    </div>

    <div class="space-y-6">
      <!-- Embed Script -->
      <div
        class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
      >
        <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ t('embedScript') }}
        </h3>
        <p class="mb-3 text-sm text-gray-600 dark:text-gray-400">
          Place this code in the
          <code class="rounded bg-gray-100 px-1 py-0.5 dark:bg-neutral-700">&lt;body&gt;</code>
          element:
        </p>
        <div class="relative">
          <pre
            class="overflow-x-auto rounded-lg bg-gray-50 p-4 text-xs text-gray-800 dark:bg-neutral-900
              dark:text-gray-200"
            >{{ embedScript }}</pre
          >
          <button
            type="button"
            class="absolute right-2 top-2 flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-sm font-medium
              text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:bg-neutral-700 dark:text-gray-200
              dark:hover:bg-neutral-600"
            @click="copyScript"
          >
            <Copy class="h-4 w-4" />
            {{ t('copyScript') }}
          </button>
        </div>
      </div>

      <!-- Chat Functions -->
      <div
        class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
      >
        <h3 class="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ t('chatFunctions') }}
        </h3>
        <p class="mb-3 text-sm text-gray-600 dark:text-gray-400">
          {{ t('chatFunctionsDesc') }}
        </p>
        <div class="space-y-2 text-sm">
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-700 dark:text-gray-300">{{ t('openChat') }}:</span>
            <code class="rounded bg-gray-100 px-2 py-1 dark:bg-neutral-700">
              window.jupus.chatOpen()
            </code>
          </div>
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-700 dark:text-gray-300">{{ t('closeChat') }}:</span>
            <code class="rounded bg-gray-100 px-2 py-1 dark:bg-neutral-700">
              window.jupus.chatClose()
            </code>
          </div>
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-700 dark:text-gray-300">{{ t('toggleChat') }}:</span>
            <code class="rounded bg-gray-100 px-2 py-1 dark:bg-neutral-700">
              window.jupus.chatToggle()
            </code>
          </div>
        </div>
      </div>

      <!-- Button Functions -->
      <div
        class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
      >
        <h3 class="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ t('buttonFunctions') }}
        </h3>
        <p class="mb-3 text-sm text-gray-600 dark:text-gray-400">
          {{ t('buttonFunctionsDesc') }}
        </p>
        <div class="space-y-2 text-sm">
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-700 dark:text-gray-300">{{ t('showButton') }}:</span>
            <code class="rounded bg-gray-100 px-2 py-1 dark:bg-neutral-700">
              window.jupus.buttonShow()
            </code>
          </div>
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-700 dark:text-gray-300">{{ t('hideButton') }}:</span>
            <code class="rounded bg-gray-100 px-2 py-1 dark:bg-neutral-700">
              window.jupus.buttonHide()
            </code>
          </div>
          <div class="flex items-center gap-2">
            <span class="font-medium text-gray-700 dark:text-gray-300">
              {{ t('toggleButton') }}:
            </span>
            <code class="rounded bg-gray-100 px-2 py-1 dark:bg-neutral-700">
              window.jupus.buttonToggle()
            </code>
          </div>
        </div>
      </div>

      <!-- Chat Settings -->
      <div
        class="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ t('chatSettings') }}
        </h3>
        <CheckboxField
          v-model="org.bot_chat_auto_open"
          :label="t('autoOpenChat')"
          name="bot_chat_auto_open"
        />
        <InputField
          v-if="org.bot_chat_auto_open"
          v-model="org.bot_chat_auto_open_delay"
          type="number"
          :label="t('autoOpenDelay')"
          name="bot_chat_auto_open_delay"
          :rules="yup.number().min(0)"
        />

        <!-- Initial Messages -->
        <div class="mt-4">
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ t('initialMessages') }}
          </label>
          <div class="space-y-2">
            <div
              v-for="(message, index) in initialMessages"
              :key="index"
              class="flex items-center gap-2"
            >
              <input
                v-model="message.text"
                type="text"
                :placeholder="t('messageText')"
                class="block flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800
                  dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100"
              />
              <button
                type="button"
                class="rounded-md border border-red-300 bg-white p-2 text-red-600 transition-colors hover:bg-red-50
                  dark:border-red-700 dark:bg-neutral-700 dark:text-red-400 dark:hover:bg-red-900/20"
                @click="removeMessage(index)"
              >
                <Trash2 class="h-4 w-4" />
              </button>
            </div>
          </div>
          <button
            type="button"
            class="mt-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700
              transition-colors hover:bg-gray-50 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-200
              dark:hover:bg-neutral-600"
            @click="addMessage"
          >
            {{ t('addMessage') }}
          </button>
        </div>

        <!-- Initial Buttons -->
        <div class="mt-4">
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ t('initialButtons') }}
          </label>
          <div class="space-y-2">
            <div
              v-for="(button, index) in initialButtons"
              :key="index"
              class="flex items-start gap-2"
            >
              <div class="flex flex-1 flex-col gap-2 sm:flex-row">
                <input
                  v-model="button.title"
                  type="text"
                  :placeholder="t('buttonText')"
                  class="block flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800
                    dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100"
                />
                <input
                  v-model="button.payload"
                  type="url"
                  :placeholder="t('buttonLink')"
                  class="block flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800
                    dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-100"
                />
              </div>
              <button
                type="button"
                class="mt-0.5 rounded-md border border-red-300 bg-white p-2 text-red-600 transition-colors hover:bg-red-50
                  dark:border-red-700 dark:bg-neutral-700 dark:text-red-400 dark:hover:bg-red-900/20"
                @click="removeButton(index)"
              >
                <Trash2 class="h-4 w-4" />
              </button>
            </div>
          </div>
          <button
            type="button"
            class="mt-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700
              transition-colors hover:bg-gray-50 dark:border-neutral-600 dark:bg-neutral-700 dark:text-gray-200
              dark:hover:bg-neutral-600"
            @click="addButton"
          >
            {{ t('addButton') }}
          </button>
        </div>
      </div>

      <!-- Design & Colors -->
      <div
        class="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ t('design') }}
        </h3>

        <h4 class="mb-3 font-medium text-gray-800 dark:text-gray-200">Header</h4>
        <FormGrid>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_title_background_color"
              :label="t('headerBackgroundColor')"
              name="bot_title_background_color"
            />
          </div>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_title_text_color"
              :label="t('headerTextColor')"
              name="bot_title_text_color"
            />
          </div>
        </FormGrid>

        <h4 class="mb-3 mt-4 font-medium text-gray-800 dark:text-gray-200">Chat Bubbles</h4>
        <FormGrid>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_chat_bubble_operator_color"
              :label="t('botBubbleColor')"
              name="bot_chat_bubble_operator_color"
            />
          </div>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_chat_bubble_operator_text_color"
              :label="t('botBubbleTextColor')"
              name="bot_chat_bubble_operator_text_color"
            />
          </div>
        </FormGrid>
        <FormGrid>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_chat_bubble_contact_color"
              :label="t('userBubbleColor')"
              name="bot_chat_bubble_contact_color"
            />
          </div>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_chat_bubble_contact_text_color"
              :label="t('userBubbleTextColor')"
              name="bot_chat_bubble_contact_text_color"
            />
          </div>
        </FormGrid>

        <h4 class="mb-3 mt-4 font-medium text-gray-800 dark:text-gray-200">Background & Input</h4>
        <FormGrid>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_background_color"
              :label="t('chatBackgroundColor')"
              name="bot_background_color"
            />
          </div>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_message_input_background_color"
              :label="t('inputBackgroundColor')"
              name="bot_message_input_background_color"
            />
          </div>
        </FormGrid>
        <ColorPickerField
          v-model="org.bot_message_input_text_color"
          :label="t('inputTextColor')"
          name="bot_message_input_text_color"
        />

        <h4 class="mb-3 mt-4 font-medium text-gray-800 dark:text-gray-200">Typography & Footer</h4>
        <InputField
          v-model="org.bot_custom_font_family"
          :label="t('customFont')"
          name="bot_custom_font_family"
          placeholder="e.g., Arial, Helvetica, sans-serif"
        />
        <TextAreaField
          v-model="org.bot_footer_content"
          :label="t('footerContent')"
          name="bot_footer_content"
          :rows="3"
          placeholder="<p>Custom footer HTML...</p>"
        />
      </div>

      <!-- Website Button -->
      <div
        class="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ t('websiteButton') }}
        </h3>

        <SelectField
          v-model="org.bot_button_position"
          name="bot_button_position"
          :label="t('buttonPosition')"
          :options="buttonPositionOptions"
        />

        <FormGrid>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_button_background_color"
              :label="t('buttonBackgroundColor')"
              name="bot_button_background_color"
            />
          </div>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_button_text_color"
              :label="t('buttonTextColor')"
              name="bot_button_text_color"
            />
          </div>
        </FormGrid>

        <h4 class="mb-3 mt-4 font-medium text-gray-800 dark:text-gray-200">
          {{ t('customButton') }}
        </h4>
        <InputField
          v-model="org.bot_floating_custom_button_text"
          :label="t('customButtonText')"
          name="bot_floating_custom_button_text"
          placeholder="Chat with us!"
        />
        <FormGrid>
          <div class="col-span-6">
            <InputField
              v-model="org.bot_floating_custom_button_width"
              type="number"
              :label="t('customButtonWidth')"
              name="bot_floating_custom_button_width"
              :rules="yup.number().min(0)"
            />
          </div>
          <div class="col-span-6">
            <InputField
              v-model="org.bot_floating_custom_button_height"
              type="number"
              :label="t('customButtonHeight')"
              name="bot_floating_custom_button_height"
              :rules="yup.number().min(0)"
            />
          </div>
        </FormGrid>
        <FormGrid>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_floating_custom_button_background_color"
              :label="t('customButtonBgColor')"
              name="bot_floating_custom_button_background_color"
            />
          </div>
          <div class="col-span-6">
            <ColorPickerField
              v-model="org.bot_floating_custom_button_text_color"
              :label="t('customButtonTextColor')"
              name="bot_floating_custom_button_text_color"
            />
          </div>
        </FormGrid>
        <TextAreaField
          v-model="org.bot_floating_custom"
          :label="t('customButtonCSS')"
          name="bot_floating_custom"
          :rows="4"
          placeholder="Custom CSS for the floating button..."
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Additional styles if needed */
</style>
